'use client';

import {ChevronLeftIcon, ChevronRightIcon, ChevronDownIcon} from 'lucide-react';
import {type ComponentProps, useEffect, useRef} from 'react';
import {DayPicker, getDefaultClassNames, type DayButton, type Locale} from 'react-day-picker';

import {Button, buttonVariants} from './button';
import {cn} from '../../lib/utils';

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = 'label',
  buttonVariant = 'primary',
  locale,
  formatters,
  components,
  dir,
  ...props
}: ComponentProps<typeof DayPicker> & {
  buttonVariant?: ComponentProps<typeof Button>['variant'];
}) {
  const defaultClassNames = getDefaultClassNames();

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn(
        'p-2 [--cell-radius:var(--radius-md)] [--cell-size:--spacing(7)] group/calendar bg-background in-data-[slot=card-content]:bg-transparent in-data-[slot=popover-content]:bg-transparent',
        className
      )}
      captionLayout={captionLayout}
      locale={locale}
      dir={dir}
      formatters={{
        formatMonthDropdown: date => date.toLocaleString(locale?.code, {month: 'short'}),
        ...formatters
      }}
      classNames={{
        root: cn('w-fit', defaultClassNames.root),
        months: cn('relative flex flex-col gap-4 md:flex-row', defaultClassNames.months),
        month: cn('flex w-full flex-col gap-4', defaultClassNames.month),
        nav: cn(
          'absolute inset-x-0 top-0 flex w-full items-center justify-between gap-1',
          defaultClassNames.nav
        ),
        button_previous: cn(
          buttonVariants({variant: buttonVariant}),
          'size-(--cell-size) p-0 select-none aria-disabled:opacity-50',
          defaultClassNames.button_previous
        ),
        button_next: cn(
          buttonVariants({variant: buttonVariant}),
          'size-(--cell-size) p-0 select-none aria-disabled:opacity-50',
          defaultClassNames.button_next
        ),
        month_caption: cn(
          'flex h-(--cell-size) w-full items-center justify-center px-(--cell-size)',
          defaultClassNames.month_caption
        ),
        dropdowns: cn(
          'flex h-(--cell-size) w-full items-center justify-center gap-6 text-sm font-semibold',
          defaultClassNames.dropdowns
        ),
        dropdown_root: cn(
          'cn-calendar-dropdown-root relative cursor-pointer rounded-(--cell-radius)',
          defaultClassNames.dropdown_root
        ),
        dropdown: cn(
          'absolute inset-0 cursor-pointer bg-popover opacity-0',
          defaultClassNames.dropdown
        ),
        caption_label: cn(
          'select-none',
          captionLayout === 'label'
            ? 'text-sm font-semibold'
            : 'cn-calendar-caption-label flex cursor-pointer items-center gap-1 rounded-(--cell-radius) text-sm font-semibold text-fg [&>svg]:size-3.5 [&>svg]:text-fg-quaternary',
          defaultClassNames.caption_label
        ),
        table: 'w-full border-collapse',
        weekdays: cn('flex', defaultClassNames.weekdays),
        weekday: cn(
          'flex-1 rounded-(--cell-radius) text-calendar-weekday font-normal text-fg-tertiary select-none',
          defaultClassNames.weekday
        ),
        week: cn('mt-2 flex w-full', defaultClassNames.week),
        week_number_header: cn('w-(--cell-size) select-none', defaultClassNames.week_number_header),
        week_number: cn(
          'text-calendar-weekday text-fg-tertiary select-none',
          defaultClassNames.week_number
        ),
        day: cn(
          'group/day relative aspect-square h-full w-full rounded-(--cell-radius) p-0 text-center select-none [&:last-child[data-selected=true]_button]:rounded-r-(--cell-radius)',
          props.showWeekNumber
            ? '[&:nth-child(2)[data-selected=true]_button]:rounded-l-(--cell-radius)'
            : '[&:first-child[data-selected=true]_button]:rounded-l-(--cell-radius)',
          defaultClassNames.day
        ),
        range_start: cn('relative isolate z-0', defaultClassNames.range_start),
        range_middle: cn('rounded-none', defaultClassNames.range_middle),
        range_end: cn('relative isolate z-0', defaultClassNames.range_end),
        today: cn('rounded-(--cell-radius)', defaultClassNames.today),
        outside: cn('text-fg-tertiary aria-selected:text-fg-tertiary', defaultClassNames.outside),
        disabled: cn('text-fg-disabled opacity-50', defaultClassNames.disabled),
        hidden: cn('invisible', defaultClassNames.hidden),
        ...classNames
      }}
      components={{
        Root: ({className, rootRef, ...props}) => {
          return <div data-slot="calendar" ref={rootRef} className={cn(className)} {...props} />;
        },
        Chevron: ({className, orientation, ...chevronProps}) => {
          const isRtl = dir === 'rtl';
          const showLeft = (orientation === 'left' && !isRtl) || (orientation === 'right' && isRtl);
          const showRight =
            (orientation === 'right' && !isRtl) || (orientation === 'left' && isRtl);

          if (showLeft) {
            return <ChevronLeftIcon className={cn('size-4', className)} {...chevronProps} />;
          }

          if (showRight) {
            return <ChevronRightIcon className={cn('size-4', className)} {...chevronProps} />;
          }

          return <ChevronDownIcon className={cn('size-4', className)} {...chevronProps} />;
        },
        DayButton: ({...props}) => <CalendarDayButton locale={locale} {...props} />,
        WeekNumber: ({children, ...props}) => {
          return (
            <td {...props}>
              <div className="flex size-(--cell-size) items-center justify-center text-center">
                {children}
              </div>
            </td>
          );
        },
        ...components
      }}
      {...props}
    />
  );
}

function CalendarDayButton({
  className,
  day,
  modifiers,
  locale,
  ...props
}: ComponentProps<typeof DayButton> & {locale?: Partial<Locale>}) {
  const defaultClassNames = getDefaultClassNames();

  const ref = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (modifiers.focused) ref.current?.focus();
  }, [modifiers.focused]);

  const isRangeMember = modifiers.range_start || modifiers.range_end || modifiers.range_middle;
  const isSingleSelected = modifiers.selected && !isRangeMember;

  return (
    <Button
      ref={ref}
      variant="tertiary"
      size="icon"
      data-day={day.date.toLocaleDateString(locale?.code)}
      data-selected-single={isSingleSelected}
      data-range-start={modifiers.range_start}
      data-range-end={modifiers.range_end}
      data-range-middle={modifiers.range_middle}
      className={cn(
        'relative isolate z-10 flex aspect-square size-auto w-full min-w-(--cell-size) flex-col gap-1 rounded-full border-0 text-fg-secondary leading-none font-normal transition-none [&>span]:text-xs [&>span]:opacity-70',
        modifiers.outside && 'text-fg-tertiary opacity-50',
        modifiers.disabled && 'pointer-events-none text-fg-disabled opacity-50',
        modifiers.today &&
          !modifiers.selected &&
          !isRangeMember &&
          'bg-background-surface_hover text-fg',
        modifiers.range_middle && 'rounded-none bg-background-surface_hover text-fg',
        (isSingleSelected || modifiers.range_start || modifiers.range_end) &&
          'bg-brand-primary text-fg_on-brand hover:bg-brand-primary hover:text-fg_on-brand',
        modifiers.range_start && 'rounded-s-full rounded-e-none',
        modifiers.range_end && 'rounded-e-full rounded-s-none',
        defaultClassNames.day,
        className
      )}
      {...props}
    />
  );
}

export {Calendar, CalendarDayButton};
